import { SetStateAction, useEffect, useState } from "react";

import { EdgePreviewAndEditStage } from "./EdgePreviewAndEdit";
import { useRetestEdgePinchGet } from "../../../projectManagementQueries";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { EdgeRetestFinalSubmitStage } from "./EdgeRetestFinalSubmitStage";
import { EdgePinchRetestPreview } from "./EdgePinchRetestPreview";
import { EdgePinchRetestFileUpload } from "./EdgePinchRetestFileUpload";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
  },

  pageTitle: {
    fontSize: 40,
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: "#111827",
  },

  subTitle: {
    fontSize: 18,
    color: "#4B5563",
    marginBottom: theme.spacing(5),
    maxWidth: 900,
    lineHeight: 1.6,
  },

  stepCard: {
    borderRadius: 24,
    padding: theme.spacing(4),
    minHeight: 180,
    border: "1px solid #E5E7EB",
    position: "relative",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  activeStep: {
    border: "2px solid #0F5C66",
    boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
  },

  stepNumber: {
    fontSize: 30,
    color: "#8B93A7",
    fontWeight: 300,
    marginBottom: theme.spacing(2),
  },

  stepLabel: {
    fontSize: 12,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: "#8B93A7",
    marginBottom: theme.spacing(1),
  },

  stepTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: "#111827",
  },

  stepDesc: {
    fontSize: 14,
    color: "#4B5563",
  },

  chip: {
    position: "absolute",
    top: 24,
    right: 24,
    borderRadius: 30,
    fontWeight: 600,
    letterSpacing: 1,
  },

  contentWrapper: {
    marginTop: theme.spacing(5),
  },
}));

export const EdgeRetestComponent = ({
  procedure,
  onSuccess,
  serialnumber,
}: any) => {
  const classes = useStyles();

  const [step, setStep] = useState(1);

  const [flowType, setFlowType] = useState<"file" | "manual" | "">("");

  const [tableData, setTableData] = useState<any[]>([]);
  const [tableDatashow, setTableDatashow] = useState<any>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<"file" | "manual" | "">(
    "",
  );

  const { data: retestData, isLoading } = useRetestEdgePinchGet(
    serialnumber,
    flowType === "manual",
  );

  const getStatus = (cardStep: number) => {
    if (step > cardStep) {
      return {
        label: "DONE",
        color: "#0F5C66",
        bg: "#D7F0EE",
      };
    }

    if (step === cardStep) {
      return {
        label: "REVIEWING",
        color: "#6B7280",
        bg: "#F3F4F6",
      };
    }

    return {
      label: "PENDING",
      color: "#9CA3AF",
      bg: "#F3F4F6",
    };
  };

  const steps =
    flowType === "manual"
      ? [
          {
            number: "02",
            label: "STEP 2",
            title: "Review & Edit",
            desc: "Review and update retest measurements",
          },
          {
            number: "03",
            label: "STEP 3",
            title: "Submit Retest",
            desc: "Save retest results and notify stakeholders",
          },
        ]
      : [
          {
            number: "01",
            label: "STEP 1",
            title: "Load Existing Data",
            desc: "Fetch original Edge Pinch measurements",
          },
          {
            number: "02",
            label: "STEP 2",
            title: "Review & Edit",
            desc: "Review and update retest measurements",
          },
          {
            number: "03",
            label: "STEP 3",
            title: "Submit Retest",
            desc: "Save retest results and notify stakeholders",
          },
        ];

  useEffect(() => {
    if (flowType === "manual" && retestData) {
      setTableData(retestData.data);
      setTableDatashow(retestData);
    }
  }, [retestData, flowType]);

  useEffect(() => {
    if (flowType === "file") {
      setStep(1);
    } else if (flowType === "manual") {
      setStep(2);
    }
  }, [flowType]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      {!flowType && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Retest Method
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 16 }}
            >
              Choose how you would like to perform the Edge Pinch retest.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={selectedMethod === "file" ? 4 : 1}
                  onClick={() => setSelectedMethod("file")}
                  style={{
                    cursor: "pointer",
                    padding: 24,
                    textAlign: "center",
                    border:
                      selectedMethod === "file"
                        ? "2px solid #F9A602"
                        : "1px solid #E0E0E0",
                    background:
                      selectedMethod === "file" ? "#FFF8E6" : "#FFFFFF",
                    position: "relative",
                  }}
                >
                  {selectedMethod === "file" && (
                    <CheckCircleIcon
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        color: "#F9A602",
                      }}
                    />
                  )}

                  <CloudUploadIcon
                    style={{
                      fontSize: 50,
                      color: "#F9A602",
                      marginBottom: 12,
                    }}
                  />

                  <Typography variant="h6">File Upload</Typography>

                  <Typography variant="body2" color="textSecondary">
                    Upload retest data from CSV or Excel files
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={selectedMethod === "manual" ? 4 : 1}
                  onClick={() => setSelectedMethod("manual")}
                  style={{
                    cursor: "pointer",
                    padding: 24,
                    textAlign: "center",
                    border:
                      selectedMethod === "manual"
                        ? "2px solid #F9A602"
                        : "1px solid #E0E0E0",
                    background:
                      selectedMethod === "manual" ? "#FFF8E6" : "#FFFFFF",
                    position: "relative",
                  }}
                >
                  {selectedMethod === "manual" && (
                    <CheckCircleIcon
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        color: "#F9A602",
                      }}
                    />
                  )}

                  <EditIcon
                    style={{
                      fontSize: 50,
                      color: "#F9A602",
                      marginBottom: 12,
                    }}
                  />

                  <Typography variant="h6">Manual Entry</Typography>

                  <Typography variant="body2" color="textSecondary">
                    Load existing data and enter retest values manually
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!selectedMethod}
                onClick={() => setFlowType(selectedMethod)}
                style={{
                  backgroundColor: "#F9A602",
                  color: "black",
                  minWidth: 120,
                }}
              >
                Continue
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <Container maxWidth="xl" className={classes.root}>
        {flowType && (
          <>
            <Box mb={6}>
              <Typography className={classes.pageTitle}>
                Edge Pinch Retest
              </Typography>
            </Box>

            <Paper elevation={4} style={{ padding: 10 }}>
              {flowType === "file" && (
                <Grid container spacing={3}>
                  {steps.map((item, index) => {
                    const status = getStatus(index + 1);

                    return (
                      <Grid item xs={12} md={4} key={item.number}>
                        <Card
                          className={`${classes.stepCard} ${
                            step === index + 1 ? classes.activeStep : ""
                          }`}
                          elevation={0}
                        >
                          <Chip
                            label={status.label}
                            className={classes.chip}
                            style={{
                              background: status.bg,
                              color: status.color,
                            }}
                          />

                          <Typography className={classes.stepNumber}>
                            {item.number}
                          </Typography>

                          <Typography className={classes.stepLabel}>
                            {item.label}
                          </Typography>

                          <Typography className={classes.stepTitle}>
                            {item.title}
                          </Typography>

                          <Typography className={classes.stepDesc}>
                            {item.desc}
                          </Typography>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              <Box className={classes.contentWrapper}>
                <Card>
                  <CardContent>
                    {step === 1 && flowType === "file" && (
                      <EdgePinchRetestFileUpload
                        serialnumber={serialnumber}
                        selectedFile={selectedFile}
                        setSelectedFile={setSelectedFile}
                        setTableData={setTableData}
                        setTableDatashow={setTableDatashow}
                        onContinue={() => setStep(2)}
                      />
                    )}

                    {step === 2 && (
                      <EdgePinchRetestPreview
                        tableData={tableData}
                        setTableData={setTableData}
                        onContinue={() => setStep(3)}
                      />
                    )}

                    {step === 3 && (
                      <EdgeRetestFinalSubmitStage
                        showdata={tableDatashow}
                        tableData={tableData}
                        onBack={() => setStep(2)}
                        onSuccess={onSuccess}
                      />
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </>
        )}
      </Container>
    </>
  );
};
